// tRPC Client Setup

import { createTRPCReact, httpBatchLink } from '@trpc/react-query';
import { QueryClient } from '@tanstack/react-query';
import type { AppRouter } from '@calculom/api/router-type';
import { useAuthStore } from '../stores/auth-store';

export const trpc = createTRPCReact<AppRouter>();

// Default to same-origin /trpc unless an explicit API URL is configured.
const API_BASE_URL = (import.meta.env.VITE_API_URL || '').replace(/\/+$/, '');
const TRPC_BASE_URL = API_BASE_URL ? `${API_BASE_URL}/trpc` : '/trpc';
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Mutex for token refresh to prevent concurrent refresh attempts.
// When multiple requests get 401 simultaneously (e.g., batch link),
// only one refresh should execute; others wait for its result.
let refreshPromise: Promise<boolean> | null = null;

export async function tryRefreshToken(): Promise<boolean> {
  // If a refresh is already in progress, wait for it
  if (refreshPromise) {
    return refreshPromise;
  }

  const { refreshToken, setTokens, logout } = useAuthStore.getState();

  if (!refreshToken) {
    logout();
    return false;
  }

  refreshPromise = (async () => {
    try {
      // Use non-batched tRPC wire format for refresh to avoid batch payload edge cases.
      const refreshResponse = await fetch(`${TRPC_BASE_URL}/auth.refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          json: { refreshToken },
        }),
      });

      if (refreshResponse.ok) {
        const data = await refreshResponse.json();
        const envelope = Array.isArray(data) ? data[0] : data;
        const result = envelope?.result?.data?.json;

        if (result?.accessToken && result?.refreshToken) {
          setTokens(result.accessToken, result.refreshToken, result.expiresAt);
          return true;
        }
      }

      // Refresh failed - logout
      logout();
      return false;
    } catch {
      logout();
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: TRPC_BASE_URL,
      async headers() {
        const { accessToken } = useAuthStore.getState();
        return accessToken
          ? {
              Authorization: `Bearer ${accessToken}`,
            }
          : {};
      },
      async fetch(url, options) {
        const response = await fetch(url, options);

        // Handle 401 - try to refresh token
        if (response.status === 401) {
          const refreshed = await tryRefreshToken();

          if (refreshed) {
            // Retry original request with new token
            const { accessToken } = useAuthStore.getState();
            const newOptions = {
              ...options,
              headers: {
                ...options?.headers,
                Authorization: `Bearer ${accessToken}`,
              },
            };
            return fetch(url, newOptions);
          }
        }

        return response;
      },
    }),
  ],
});
